import { useCallback, useState } from 'react';

import { Provider } from '@/features/game-context/types';
import { getErrorType } from '@/features/game-window-integration/helpers/error-mapping';
import { getErrorMessage } from '@/features/game-window-integration/providers/aristocrat/error-helpers';
import {
  AristocratEvent,
  AristocratEventData,
} from '@/features/game-window-integration/providers/aristocrat/types';
import { AristocratEvents } from '@/features/game-window-integration/types';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';

const useAristocratHandlers = () => {
  const [gameHandler, setGameHandler] = useState<MessageEventSource>();

  const handleGameReady = useCallback((aristocratEvent: AristocratEvent) => {
    WrapperProxy.setGameLoaded();
    const { rawEvent } = aristocratEvent;

    const handler = rawEvent.source;
    if (!handler) return;

    setGameHandler(rawEvent?.source);
  }, []);

  const handleSpinInProgress = useCallback(
    ({ data }: { data: AristocratEventData }) =>
      WrapperProxy.setSpinInProgress(
        data.event === AristocratEvents.SPIN_START,
      ),
    [],
  );

  const handleVolumeChange = useCallback(
    ({ data }: { data: AristocratEventData }) =>
      WrapperProxy.setAudio(data.event === AristocratEvents.SOUND_ON),
    [],
  );

  const handleShowDeposit = useCallback(() => WrapperProxy.showFunds(), []);

  const handleGameReset = useCallback(() => WrapperProxy.resetGame(), []);

  const handleError = useCallback((payload) => {
    const errorCode = payload?.data?.event;
    let errorMessage = payload.data?.payload;

    if (
      !errorMessage ||
      (typeof errorMessage === 'object' &&
        Object.keys(errorMessage).length === 0)
    ) {
      errorMessage = getErrorMessage(errorCode);
    }
    const errorType = getErrorType(
      errorCode,
      Provider.ARISTOCRAT,
      false,
      payload,
    );

    WrapperProxy.setProviderError({
      display: true,
      errorCode,
      errorMessage,
      errorType,
    });
  }, []);

  return {
    handleGameReady,
    handleSpinInProgress,
    handleShowDeposit,
    handleVolumeChange,
    handleGameReset,
    handleError,
    gameHandler,
  };
};

export default useAristocratHandlers;
